/**
 * Sales Order 狀態（2026-09：DRAFT → PREPARING → SHIPPING → …；terminate RETIRED）。
 */

import { ROUTES } from '#/config/routes';

export const SALES_ORDER_STATUS = {
  DRAFT: 'DRAFT',
  PREPARING: 'PREPARING',
  SHIPPING: 'SHIPPING',
  DELIVERED: 'DELIVERED',
  INVOICED: 'INVOICED',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
  TERMINATED: 'TERMINATED',
};

export const SALES_ORDER_STATUS_LABELS = {
  DRAFT: '草稿',
  PREPARING: '備貨中',
  SHIPPING: '出貨中',
  DELIVERED: '已送達',
  INVOICED: '已開票',
  PAID: '已收款',
  CANCELLED: '已取消',
  TERMINATED: '已中止',
  SUBMITTED: '已提交',
  APPROVED: '已核准',
};

export const SALES_ORDER_STATUS_FILTER_OPTIONS = [
  { value: 'DRAFT', label: '草稿' },
  { value: 'PREPARING', label: '備貨中' },
  { value: 'SHIPPING', label: '出貨中' },
  { value: 'DELIVERED', label: '已送達' },
  { value: 'INVOICED', label: '已開票' },
  { value: 'PAID', label: '已收款' },
  { value: 'CANCELLED', label: '已取消' },
];

/** 寫死色票：深底淺字 */
export const SALES_ORDER_STATUS_CLASS = {
  DRAFT: 'border-transparent bg-amber-600 text-amber-50',
  PREPARING: 'border-transparent bg-slate-600 text-slate-50',
  SHIPPING: 'border-transparent bg-sky-600 text-sky-50',
  DELIVERED: 'border-transparent bg-teal-600 text-teal-50',
  INVOICED: 'border-transparent bg-violet-600 text-violet-50',
  PAID: 'border-transparent bg-emerald-600 text-emerald-50',
  CANCELLED: 'border-transparent bg-rose-600 text-rose-50',
  TERMINATED: 'border-transparent bg-rose-700 text-rose-50',
};

export function formatSalesOrderStatusLabel(status) {
  if (status == null || status === '') {
    return '—';
  }
  return SALES_ORDER_STATUS_LABELS[status] ?? status;
}

/** 僅 DRAFT 可 PATCH 主內容／明細。 */
export function canEditSalesOrderDraft(status) {
  return status === SALES_ORDER_STATUS.DRAFT;
}

/** @deprecated 改用 canEditSalesOrderDraft */
export function canEditSalesOrderPreparing(status) {
  return canEditSalesOrderDraft(status);
}

/** 任意狀態可 PATCH 備註。 */
export function canUpdateSalesOrderRemark(status) {
  return Boolean(status);
}

/** DRAFT 可提交。 */
export function canSubmitSalesOrder(status) {
  return status === SALES_ORDER_STATUS.DRAFT;
}

/** PREPARING 可退回草稿。 */
export function canReturnSalesOrderToDraft(status) {
  return status === SALES_ORDER_STATUS.PREPARING;
}

/** PREPARING 可出貨。 */
export function canShipSalesOrder(status) {
  return status === SALES_ORDER_STATUS.PREPARING;
}

/** SHIPPING 可退回備貨。 */
export function canReturnSalesOrderToPreparing(status) {
  return status === SALES_ORDER_STATUS.SHIPPING;
}

/** SHIPPING 可送達。 */
export function canDeliverSalesOrder(status) {
  return status === SALES_ORDER_STATUS.SHIPPING;
}

/** DELIVERED 且有 stockIssueId 才顯示（契約多半無 SI）。 */
export function canShowSalesOrderStockIssue(status, stockIssueId) {
  return status === SALES_ORDER_STATUS.DELIVERED && Boolean(stockIssueId);
}

/** DELIVERED 開票；INVOICED 可再開票修正四欄。 */
export function canInvoiceSalesOrder(status) {
  return (
    status === SALES_ORDER_STATUS.DELIVERED
    || status === SALES_ORDER_STATUS.INVOICED
  );
}

/** INVOICED 可收款。 */
export function canReceiveSalesOrderPayment(status) {
  return status === SALES_ORDER_STATUS.INVOICED;
}

/** 僅 DRAFT 可取消。 */
export function canCancelSalesOrder(status) {
  return status === SALES_ORDER_STATUS.DRAFT;
}

/** terminate RETIRED：前端永遠不露。 */
export function canTerminateSalesOrder() {
  return false;
}

/** DELETE 軟取消別名：僅 DRAFT。 */
export function canDeleteSalesOrder(status) {
  return canCancelSalesOrder(status);
}

/** DRAFT／PREPARING 可進配置庫存頁。 */
export function canAllocateSalesOrderInventory(status) {
  return (
    status === SALES_ORDER_STATUS.DRAFT
    || status === SALES_ORDER_STATUS.PREPARING
  );
}

/** 有 SI 詳情路由才回 path；否則 null。 */
export function getSalesOrderStockIssueDetailPath(stockIssueId) {
  if (!stockIssueId || typeof ROUTES.stockIssueDetail !== 'function') {
    return null;
  }
  return ROUTES.stockIssueDetail(stockIssueId);
}
